import React, {
  forwardRef,
  MutableRefObject,
  useImperativeHandle,
} from 'react';
import { FormControl, PhoneInput } from '@tangibleplay/core-ui';

import { fieldNames, getFieldsConfig } from './InformationContactForm.config';

import { StyledInformationContactForm } from './InformationContactForm.styles';

import {
  FieldNamesType,
  InformationContactFormProps,
} from './InformationContactForm.types';

import { validateInformationContactFormFields } from './utils';
import DefaultInputForForms from '../../_utils/DefaultInputForForms';

//TODO Remove Duplicate "customClassName" prop (duplicate of "customClassnames")
const InformationContactForm = forwardRef(
  (
    {
      // Events
      onChange = () => {},
      onFieldChange = () => {},
      // Fields data
      defaultCountry,
      values = {},
      labels,
      placeholders,
      errors,
      fieldsExtraProps = {},
      limitedCountryList,
      // Tools
      requiredFields = [fieldNames.EMAIL_ADDRESS, fieldNames.PHONE_NUMBER],
      disabledFields = [],
      fieldsToHide = [],
      // Components
      inputComponent: Field = DefaultInputForForms,
      phoneInputComponent: PhoneInputComponent = PhoneInput,
      formControlComponent: FormControlComponent = FormControl,
      // Styles
      customFieldWrapperClassName = '',
      componentClassname = 'information-contact-form',
      customClassnames = '',
      customClassName = '',
      customStyle = '',
    }: InformationContactFormProps,
    ref,
  ) => {
    /**
     * Constants
     */
    const fieldsConfig = getFieldsConfig();
    const hideField = (fieldName: FieldNamesType) =>
      fieldsToHide.includes(fieldName);

    /**
     * Hooks
     */
    useImperativeHandle(ref, () => ({
      validateFields() {
        const errors = validateInformationContactFormFields(
          values,
          requiredFields,
        );

        return errors;
      },
    }));

    /**
     * Event handlers
     */
    const handleChange = (
      values: InformationContactFormProps['values'],
      source?: string,
    ) => {
      onChange(values, source);
    };

    const handleFieldChange = (fieldName: FieldNamesType, value: string) => {
      let additionalUpdates: InformationContactFormProps['values'] = {};

      onFieldChange(fieldName, value);

      handleChange(
        {
          ...values,
          ...additionalUpdates,
          [fieldName]: value,
        },
        'single-input-update',
      );
    };

    /**
     * Render helper functions
     */
    const renderTextField = (
      fieldName: FieldNamesType,
      ref?: MutableRefObject<any>,
    ) => {
      const otherProps =
        (fieldsExtraProps && fieldsExtraProps[fieldName]) || {};

      return (
        <Field
          ref={ref}
          name={fieldsConfig[fieldName].name}
          value={values[fieldName]}
          error={errors ? errors[fieldName] : undefined}
          onChange={(value: string) => handleFieldChange(fieldName, value)}
          autoComplete={fieldsConfig[fieldName].autoComplete}
          maxLength={fieldsConfig[fieldName].maxLength}
          label={
            (labels && labels[fieldName]) ||
            fieldsConfig[fieldName].defaultLabel
          }
          placeholder={
            (placeholders && placeholders[fieldName]) ||
            fieldsConfig[fieldName].defaultPlaceholder
          }
          required={requiredFields.includes(fieldName)}
          disabled={disabledFields.includes(fieldName)}
          format={fieldsConfig[fieldName].format}
          parse={fieldsConfig[fieldName].parse}
          {...otherProps}
        />
      );
    };

    const getFieldWrapperClasses = (fieldName: string) =>
      `${componentClassname}__field-wrapper ${componentClassname}__field-wrapper--${fieldName} ${customFieldWrapperClassName}`;

    /**
     * Render
     */
    return (
      <StyledInformationContactForm
        className={`${componentClassname} ${customClassnames} ${customClassName}`}
        customStyle={customStyle}
      >
        {!hideField(fieldNames.EMAIL_ADDRESS) && (
          <div className={getFieldWrapperClasses(fieldNames.EMAIL_ADDRESS)}>
            {renderTextField(fieldNames.EMAIL_ADDRESS)}
          </div>
        )}
        {!hideField(fieldNames.PHONE_NUMBER) && (
          <div className={getFieldWrapperClasses(fieldNames.PHONE_NUMBER)}>
            <FormControlComponent
              formControlLabel={
                (labels && labels[fieldNames.PHONE_NUMBER]) ||
                (labels && labels[fieldNames.PHONE_NUMBER] === null
                  ? ''
                  : fieldsConfig[fieldNames.PHONE_NUMBER].defaultLabel)
              }
              formControlName={fieldsConfig[fieldNames.PHONE_NUMBER].name}
              isRequired={requiredFields.includes(fieldNames.PHONE_NUMBER)}
              additionalContent={
                errors ? (
                  <span className={'form-control__error'}>
                    {errors[fieldNames.PHONE_NUMBER]}
                  </span>
                ) : undefined
              }
            >
              <PhoneInputComponent
                value={values[fieldNames.PHONE_NUMBER]}
                hasError={
                  errors ? !!errors[fieldNames.PHONE_NUMBER] : undefined
                }
                handleChange={(value: string) =>
                  handleFieldChange(fieldNames.PHONE_NUMBER, value)
                }
                autoComplete="on"
                isRequired={requiredFields.includes(fieldNames.PHONE_NUMBER)}
                isDisabled={disabledFields.includes(fieldNames.PHONE_NUMBER)}
                phoneInputPlaceholder={
                  (placeholders && placeholders[fieldNames.PHONE_NUMBER]) ||
                  fieldsConfig[fieldNames.PHONE_NUMBER].defaultPlaceholder
                }
                defaultCountry={defaultCountry}
                limitedCountryList={limitedCountryList}
                {...((fieldsExtraProps &&
                  fieldsExtraProps[fieldNames.PHONE_NUMBER]) ||
                  {})}
              />
            </FormControlComponent>
          </div>
        )}
      </StyledInformationContactForm>
    );
  },
);
InformationContactForm.displayName = 'InformationContactForm';

export default InformationContactForm;
